import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import { createCompany, updateCompany } from "~/services/admin/companies";
import AppDialog from "~/shared/ui/AppDialog";
import { BR_UFS } from "~/features/holidays-admin";
import { accessErrorMessage } from "../Access/labels";

// city/uf ficam fora de `trimmed`: são opcionais (o `valid` exige todos os campos de `trimmed`).
const EMPTY = { name: "", cnpj: "", address: "", cep: "", meuplantao: "", city: "", uf: "" };
const CITY_UF_HELP = "Usado para aplicar feriados municipais e estaduais na sazonalidade";

export default function CompanyDialog({ open, company, onClose, onSaved }) {
  const editing = Boolean(company);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm(company ? { name: company.name || "", cnpj: company.cnpj || "", address: company.address || "", cep: company.cep || "", meuplantao: "", city: company.city || "", uf: company.uf || "" } : EMPTY);
    setError(null);
  }, [open, company]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const trimmed = { name: form.name.trim(), cnpj: form.cnpj.trim(), address: form.address.trim(), cep: form.cep.trim() };
  const meuplantao = Number.parseInt(form.meuplantao, 10);
  const location = { city: form.city.trim() || null, uf: form.uf || null };
  const valid = Object.values(trimmed).every(Boolean) && (editing || (/^\d+$/.test(form.meuplantao.trim()) && meuplantao > 0));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        await updateCompany(company.id, { ...trimmed, ...location });
        onSaved("Empresa atualizada.");
      } else {
        await createCompany({ ...trimmed, meuplantao, ...location });
        onSaved("Empresa criada.");
      }
    } catch (e) {
      setError(accessErrorMessage(e, "Não foi possível salvar a empresa."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      busy={saving}
      size="sm"
      title={editing ? "Editar empresa" : "Nova empresa"}
      context={editing ? `${company.name} · #${company.id}` : undefined}
      actions={
        <>
          <Button onClick={onClose} disabled={saving} sx={{ color: "text.primary" }}>
            Cancelar
          </Button>
          <LoadingButton variant="contained" loading={saving} disabled={!valid} onClick={save}>
            Salvar
          </LoadingButton>
        </>
      }
    >
      <Box sx={{ display: "grid", gap: 2 }}>
        <TextField label="Nome" required value={form.name} onChange={set("name")} />
        <TextField label="CNPJ" required value={form.cnpj} onChange={set("cnpj")} />
        <TextField label="Endereço" required value={form.address} onChange={set("address")} />
        <TextField label="CEP" required value={form.cep} onChange={set("cep")} />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 140px", gap: 2 }}>
          <TextField label="Cidade (opcional)" value={form.city} onChange={set("city")} inputProps={{ maxLength: 120 }} helperText={CITY_UF_HELP} />
          <TextField select label="UF (opcional)" value={form.uf} onChange={set("uf")} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
            <MenuItem value="">—</MenuItem>
            {BR_UFS.map((uf) => (
              <MenuItem key={uf} value={uf}>
                {uf}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {!editing && (
          <TextField
            label="ID da empresa no Minha Escala"
            required
            value={form.meuplantao}
            onChange={set("meuplantao")}
            inputProps={{ inputMode: "numeric" }}
            helperText="Liga a empresa ao Minha Escala. Não dá para mudar depois."
          />
        )}
      </Box>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </AppDialog>
  );
}
